import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ShieldCheck, IdCard, LogIn, LogOut, Plus, Search, Loader2, UserCheck, Ticket, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { vigilanciaAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import usePermissions from '../../hooks/usePermissions';

const SUB = 'proveedores_visitas';
const ESTADO_BADGE = {
  pre_registrado: { label: 'Pre-registrado', cls: 'bg-amber-100 text-amber-700' },
  en_planta: { label: 'En planta', cls: 'bg-emerald-100 text-emerald-700' },
  finalizada: { label: 'Finalizada', cls: 'bg-slate-200 text-slate-600' },
};
const hora = (iso) => (iso ? new Date(iso).toLocaleString('es-MX', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' }) : '—');
const today = () => new Date().toISOString().slice(0, 10);

const emptyForm = {
  nombre_completo: '', empresa: '', tipo: 'visita', identificacion_tipo: '',
  area: '', anfitrion: '', motivo: '', fecha_esperada: today(),
  hora_estimada_entrada: '', hora_estimada_salida: '',
};

export default function ProveedoresVisitas() {
  const { user } = useAuth();
  const { canWrite, canAccess } = usePermissions();
  const isGuardia = !!user?.is_guardia;
  const canFull = canWrite('vigilancia', SUB);
  const canPreRegister = canAccess('vigilancia', SUB);

  const [visitas, setVisitas] = useState([]);
  const [gafetes, setGafetes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [estado, setEstado] = useState(isGuardia ? 'en_planta' : 'todos');
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [isWalkIn, setIsWalkIn] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [areas, setAreas] = useState([]);

  useEffect(() => {
    vigilanciaAPI.getAreas().then((r) => setAreas(r.data || [])).catch(() => {});
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = { search: search || undefined };
      if (estado !== 'todos') params.estado = estado;
      const [v, g] = await Promise.all([vigilanciaAPI.getVisitas(params), vigilanciaAPI.getGafetes()]);
      setVisitas(v.data || []);
      setGafetes(g.data);
    } catch {
      toast.error('Error al cargar visitas');
    } finally {
      setLoading(false);
    }
  }, [estado, search]);

  useEffect(() => { const t = setTimeout(fetchAll, 250); return () => clearTimeout(t); }, [fetchAll]);

  const openForm = (walkIn) => { setForm(emptyForm); setIsWalkIn(walkIn); setFormOpen(true); };

  const submitForm = async () => {
    if (!form.nombre_completo.trim()) { toast.error('El nombre es obligatorio'); return; }
    setSaving(true);
    try {
      await vigilanciaAPI.createVisita({ ...form, es_walk_in: isWalkIn });
      toast.success(isWalkIn ? 'Walk-in registrado' : 'Visita pre-registrada');
      setFormOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const doAction = async (fn, id, okMsg) => {
    setBusyId(id);
    try { await fn(id); toast.success(okMsg); fetchAll(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setBusyId(null); }
  };

  const disponibles = gafetes ? gafetes.disponibles : '—';
  const enPlanta = visitas.filter((v) => v.estado === 'en_planta').length;
  const delDia = visitas.filter((v) => v.fecha_esperada === today() || (v.hora_real_entrada || '').startsWith(today())).length;

  const StatCard = ({ icon: Icon, label, value, accent }) => (
    <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4" data-testid={`vis-stat-${label}`}>
      <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${accent}`}><Icon className="w-6 h-6" /></div>
      <div><p className="text-2xl font-bold text-slate-900">{value}</p><p className="text-xs text-slate-500">{label}</p></div>
    </div>
  );

  // ---------- GUARD (mobile-first) ----------
  if (isGuardia) {
    return (
      <Layout>
        <div data-testid="vis-guard-view" className="space-y-4 max-w-xl mx-auto">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Caseta · Visitas</h1>
            <Badge className="bg-blue-100 text-blue-700 text-sm px-3 py-1"><Ticket className="w-4 h-4 mr-1 inline" />{disponibles} gafetes</Badge>
          </div>
          {canFull && (
            <Button className="w-full h-14 text-base" onClick={() => openForm(true)} data-testid="vis-walkin-btn">
              <Plus className="w-5 h-5 mr-2" /> Registrar Walk-in
            </Button>
          )}
          <div className="flex gap-2">
            {['en_planta', 'pre_registrado', 'todos'].map((e) => (
              <button key={e} onClick={() => setEstado(e)}
                className={`flex-1 py-2 rounded-lg text-sm font-medium ${estado === e ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'}`}
                data-testid={`vis-guard-filter-${e}`}>
                {e === 'en_planta' ? 'En planta' : e === 'pre_registrado' ? 'Por llegar' : 'Todos'}
              </button>
            ))}
          </div>
          {loading ? <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin inline text-slate-400" /></div>
            : visitas.length === 0 ? <p className="text-center text-slate-400 py-10">Sin visitas.</p>
            : visitas.map((v) => (
              <div key={v.id} className="rounded-xl border border-slate-200 bg-white p-4 space-y-2" data-testid={`vis-guard-card-${v.folio}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-slate-900">{v.nombre_completo}</p>
                    <p className="text-xs text-slate-500">{v.empresa || 'Sin empresa'} · {v.tipo}</p>
                  </div>
                  <Badge className={ESTADO_BADGE[v.estado]?.cls}>{ESTADO_BADGE[v.estado]?.label}</Badge>
                </div>
                {v.gafete_asignado != null && <p className="text-sm text-slate-600">Gafete: <b>#{v.gafete_asignado}</b></p>}
                {v.estado === 'pre_registrado' && canFull && (
                  <div className="grid grid-cols-2 gap-2">
                    <Button className="h-12" onClick={() => doAction(vigilanciaAPI.confirmarLlegada, v.id, 'Llegada confirmada')} disabled={busyId === v.id} data-testid={`vis-confirmar-${v.folio}`}>
                      <LogIn className="w-5 h-5 mr-1" /> Confirmar
                    </Button>
                    <Button variant="outline" className="h-12 border-red-300 text-red-700" onClick={() => doAction(vigilanciaAPI.registrarSalidaVisita, v.id, 'Entrada y salida registradas')} disabled={busyId === v.id} data-testid={`vis-salida-${v.folio}`}>
                      <LogOut className="w-5 h-5 mr-1" /> Salida
                    </Button>
                  </div>
                )}
                {v.estado === 'en_planta' && canFull && (
                  <Button variant="outline" className="w-full h-12 border-red-300 text-red-700" onClick={() => doAction(vigilanciaAPI.registrarSalidaVisita, v.id, 'Salida registrada')} disabled={busyId === v.id} data-testid={`vis-salida-${v.folio}`}>
                    <LogOut className="w-5 h-5 mr-2" /> Registrar salida
                  </Button>
                )}
                {v.estado === 'finalizada' && (
                  <Button variant="ghost" className="w-full" onClick={() => setDetail(v)} data-testid={`vis-ver-${v.folio}`}>
                    <Eye className="w-4 h-4 mr-1" /> Ver detalle
                  </Button>
                )}
              </div>
            ))}
        </div>
        <VisitaFormDialog {...{ formOpen, setFormOpen, form, setForm, isWalkIn, saving, submitForm, areas }} />
        <VisitaDetailDialog detail={detail} onClose={() => setDetail(null)} />
      </Layout>
    );
  }

  // ---------- OFFICE ----------
  return (
    <Layout>
      <div data-testid="vis-office-view" className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-lg bg-slate-900 flex items-center justify-center"><ShieldCheck className="w-6 h-6 text-white" /></div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Manrope']">Proveedores / Visitas</h1>
              <p className="text-slate-500 text-sm">Vigilancia</p>
            </div>
          </div>
          {canPreRegister && (
            <Button onClick={() => openForm(false)} data-testid="vis-prereg-btn"><Plus className="w-4 h-4 mr-2" /> Pre-registrar visita</Button>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <StatCard icon={UserCheck} label="Visitas en planta" value={enPlanta} accent="bg-emerald-100 text-emerald-600" />
          <StatCard icon={Ticket} label="Gafetes disponibles" value={disponibles} accent="bg-blue-100 text-blue-600" />
          <StatCard icon={IdCard} label="Visitas del día" value={delDia} accent="bg-amber-100 text-amber-600" />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input placeholder="Buscar por nombre, empresa o folio…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="vis-search" />
          </div>
          <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
            {['todos', 'pre_registrado', 'en_planta', 'finalizada'].map((e) => (
              <button key={e} onClick={() => setEstado(e)}
                className={`px-3 py-1.5 text-sm rounded-md ${estado === e ? 'bg-white shadow text-slate-900 font-medium' : 'text-slate-500'}`}
                data-testid={`vis-filter-${e}`}>
                {e === 'todos' ? 'Todos' : ESTADO_BADGE[e].label}
              </button>
            ))}
          </div>
        </div>

        <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
          <table className="w-full text-sm" data-testid="vis-table">
            <thead className="bg-slate-50 text-slate-500">
              <tr className="text-left">
                <th className="px-4 py-3 font-medium">Folio</th>
                <th className="px-4 py-3 font-medium">Visitante</th>
                <th className="px-4 py-3 font-medium">Anfitrión</th>
                <th className="px-4 py-3 font-medium">Gafete</th>
                <th className="px-4 py-3 font-medium">Entrada</th>
                <th className="px-4 py-3 font-medium">Estado</th>
                <th className="px-4 py-3 font-medium text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
              ) : visitas.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400" data-testid="vis-empty">Sin visitas para mostrar.</td></tr>
              ) : visitas.map((v) => (
                <tr key={v.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setDetail(v)} data-testid={`vis-row-${v.folio}`}>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">{v.folio}</td>
                  <td className="px-4 py-3"><span className="font-medium text-slate-800">{v.nombre_completo}</span><span className="block text-xs text-slate-400">{v.empresa || '—'} · {v.tipo}</span></td>
                  <td className="px-4 py-3 text-slate-600">{v.anfitrion || '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{v.gafete_asignado != null ? `#${v.gafete_asignado}` : '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{hora(v.hora_real_entrada)}</td>
                  <td className="px-4 py-3"><Badge className={ESTADO_BADGE[v.estado]?.cls}>{ESTADO_BADGE[v.estado]?.label}</Badge></td>
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    {canFull && v.estado === 'pre_registrado' && (
                      <div className="inline-flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => doAction(vigilanciaAPI.confirmarLlegada, v.id, 'Llegada confirmada')} disabled={busyId === v.id} data-testid={`vis-confirmar-${v.folio}`}><LogIn className="w-4 h-4 mr-1" /> Confirmar</Button>
                        <Button size="sm" variant="outline" className="border-red-300 text-red-700" onClick={() => doAction(vigilanciaAPI.registrarSalidaVisita, v.id, 'Entrada y salida registradas')} disabled={busyId === v.id} data-testid={`vis-salida-${v.folio}`}><LogOut className="w-4 h-4 mr-1" /> Salida</Button>
                      </div>
                    )}
                    {canFull && v.estado === 'en_planta' && (
                      <Button size="sm" variant="outline" className="border-red-300 text-red-700" onClick={() => doAction(vigilanciaAPI.registrarSalidaVisita, v.id, 'Salida registrada')} disabled={busyId === v.id} data-testid={`vis-salida-${v.folio}`}><LogOut className="w-4 h-4 mr-1" /> Salida</Button>
                    )}
                    {v.estado === 'finalizada' && (
                      <Button size="sm" variant="ghost" onClick={() => setDetail(v)} data-testid={`vis-ver-${v.folio}`}><Eye className="w-4 h-4 mr-1" /> Ver</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <VisitaFormDialog {...{ formOpen, setFormOpen, form, setForm, isWalkIn, saving, submitForm, areas }} />
      <VisitaDetailDialog detail={detail} onClose={() => setDetail(null)} />
    </Layout>
  );
}

const DetailRow = ({ label, value }) => (
  <div className="flex flex-col">
    <span className="text-[11px] uppercase tracking-wide text-slate-400">{label}</span>
    <span className="text-sm text-slate-800">{value === null || value === undefined || value === '' ? '—' : value}</span>
  </div>
);

function VisitaDetailDialog({ detail, onClose }) {
  if (!detail) return null;
  const v = detail;
  return (
    <Dialog open={!!detail} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="vis-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 flex-wrap">
            <span>{v.nombre_completo}</span>
            <Badge className={ESTADO_BADGE[v.estado]?.cls}>{ESTADO_BADGE[v.estado]?.label}</Badge>
            <span className="text-xs text-slate-400 font-normal font-mono">{v.folio}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3">
          <DetailRow label="Empresa" value={v.empresa} />
          <DetailRow label="Tipo" value={v.tipo} />
          <DetailRow label="Área" value={v.area} />
          <DetailRow label="Anfitrión" value={v.anfitrion} />
          <DetailRow label="Gafete" value={v.gafete_asignado != null ? `#${v.gafete_asignado}` : null} />
          <DetailRow label="Walk-in" value={v.es_walk_in ? 'Sí' : 'No'} />
          <DetailRow label="Fecha esperada" value={v.fecha_esperada} />
          <DetailRow label="Hora est. entrada" value={v.hora_estimada_entrada} />
          <DetailRow label="Hora est. salida" value={v.hora_estimada_salida} />
          <DetailRow label="Entrada real" value={hora(v.hora_real_entrada)} />
          <DetailRow label="Salida real" value={hora(v.hora_real_salida)} />
          <DetailRow label="Identificación" value={v.identificacion?.tipo} />
          <div className="col-span-2 sm:col-span-3"><DetailRow label="Motivo" value={v.motivo} /></div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function VisitaFormDialog({ formOpen, setFormOpen, form, setForm, isWalkIn, saving, submitForm, areas = [] }) {
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  return (
    <Dialog open={formOpen} onOpenChange={(o) => !o && setFormOpen(false)}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="vis-form-dialog">
        <DialogHeader><DialogTitle>{isWalkIn ? 'Registrar Walk-in' : 'Pre-registrar visita'}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2"><Label className="text-xs text-slate-600">Nombre completo *</Label><Input value={form.nombre_completo} onChange={(e) => set('nombre_completo', e.target.value)} data-testid="vis-field-nombre" /></div>
          <div><Label className="text-xs text-slate-600">Empresa</Label><Input value={form.empresa} onChange={(e) => set('empresa', e.target.value)} data-testid="vis-field-empresa" /></div>
          <div>
            <Label className="text-xs text-slate-600">Tipo</Label>
            <Select value={form.tipo} onValueChange={(v) => set('tipo', v)}>
              <SelectTrigger data-testid="vis-field-tipo"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="visita">Visita</SelectItem>
                <SelectItem value="proveedor">Proveedor</SelectItem>
                <SelectItem value="contratista">Contratista</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-600">Área</Label>
            <Select value={form.area || ''} onValueChange={(v) => set('area', v)}>
              <SelectTrigger data-testid="vis-field-area"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
              <SelectContent>
                {areas.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs text-slate-600">Anfitrión</Label><Input value={form.anfitrion} onChange={(e) => set('anfitrion', e.target.value)} data-testid="vis-field-anfitrion" /></div>
          <div><Label className="text-xs text-slate-600">Identificación (tipo)</Label><Input value={form.identificacion_tipo} onChange={(e) => set('identificacion_tipo', e.target.value)} placeholder="INE, licencia…" data-testid="vis-field-ident" /></div>
          {!isWalkIn && <div><Label className="text-xs text-slate-600">Fecha esperada</Label><Input type="date" value={form.fecha_esperada} onChange={(e) => set('fecha_esperada', e.target.value)} data-testid="vis-field-fecha" /></div>}
          <div className="sm:col-span-2"><Label className="text-xs text-slate-600">Motivo</Label><Textarea rows={2} value={form.motivo} onChange={(e) => set('motivo', e.target.value)} data-testid="vis-field-motivo" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setFormOpen(false)} disabled={saving}>Cancelar</Button>
          <Button onClick={submitForm} disabled={saving} data-testid="vis-form-submit">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{isWalkIn ? 'Registrar walk-in' : 'Pre-registrar'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
